import { TableHead, TableRow, TableCell, useTheme, Checkbox, Box, TableSortLabel } from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import makeStyles from '@mui/styles/makeStyles';
import { useLocation } from 'react-router-dom';
import { getTabColorHeaderMain } from '../../shared/constants/constants';
import {
  EngagementKeys,
  IssueAndServiceSharedValues,
  IssueKeys,
  ServiceKeys,
} from '../../shared/constants/IssueAndServiceKeys';

const useStyles = makeStyles(({ palette, tabColor }) => ({
  tableHeaderCell: {
    textAlign: 'left',
    padding: '10px 6px 10px 10px',
    color: 'white',
    fontSize: '14px',
    fontWeight: 700,
    width: 'auto',
    whiteSpace: 'nowrap',
    backgroundColor: ({ tabColor }) => tabColor,
    position: 'relative',
  },
  tableIconCell: {
    width: '10px',
    borderLeft: 'none',
    borderRight: 'none',
    padding: 0,
  },
  wide1Col: {
    width: '20%',
  },
  wide2Col: {
    width: '40%',
  },
  teamMemberCol: {
    width: '15%',
  },
  leadExpertCol: {
    width: '10%',
  },
  unitCol: {
    width: '10%',
  },
  orgOutletDeptCol: {
    width: '10%',
  },
  centerAlignedHeaderCell: {
    textAlign: 'center',
  },
  sortLabel: {
    color: 'white',
    '&:hover': {
      color: 'white',
    },
    '&:not(.Mui-active) .MuiTableSortLabel-icon': {
      transform: 'none',
    },
    '&.Mui-active': {
      color: 'white',
    },
    '&.Mui-active .MuiTableSortLabel-icon': {
      color: 'white',
      opacity: 1,
    },
  },
  sortLabelActive: {
    color: 'white',
  },
  sortLabelIcon: {
    color: palette.grey[300],
    opacity: 1,
  },
}));

// TODO: Add unit test to make sure they are correct subarrays from constants.js
// Also tests to make sure the same column isn't in Media and Service at some time
const wide1ColsMap = {
  MediaInteraction: [IssueKeys.campaign],
  ServiceLog: [ServiceKeys.campaign],
  EngagementLog: [EngagementKeys.campaign],
};

const wide2ColsMap = {
  MediaInteraction: [IssueKeys.topic],
  ServiceLog: [ServiceKeys.service],
  EngagementLog: [EngagementKeys.topic],
};

export default function TrackerTableHeaderRow({
  columns,
  type,
  tabColor,
  isBulkEdit = false,
  isBulkEditSelectedAll = false,
  isBulkEditSelectedIndeterminate = false,
  onBulkEditSelectAll = () => {},
  sortConfig = null,
  onSort = () => {},
}) {
  const { pathname } = useLocation();
  const theme = useTheme();
  const classes = useStyles({ tabColor: tabColor ?? getTabColorHeaderMain(pathname, theme) });
  const centerAlignedColumns = ['Status', 'Complexity', 'Tone'];

  const getTableRowCellClass = (col) => {
    let cellClasses = [classes.tableHeaderCell];

    if (wide1ColsMap[type].includes(col)) {
      cellClasses.push(classes.wide1Col);
    } else if (wide2ColsMap[type].includes(col)) {
      cellClasses.push(classes.wide2Col);
    } else if (col === IssueAndServiceSharedValues.teamMembers) {
      cellClasses.push(classes.teamMemberCol);
    } else if (col === IssueAndServiceSharedValues.leadExpert) {
      cellClasses.push(classes.leadExpertCol);
    } else if (col === IssueAndServiceSharedValues.unit) {
      cellClasses.push(classes.unitCol);
    } else if (
      col === IssueKeys.outlet ||
      col === EngagementKeys.organization ||
      col === IssueAndServiceSharedValues.department
    ) {
      cellClasses.push(classes.orgOutletDeptCol);
    }

    if (centerAlignedColumns.includes(col)) {
      cellClasses.push(classes.centerAlignedHeaderCell);
    }

    return cellClasses.join(' ');
  };

  let tableHeaderSecondCol = <TableCell className={`${classes.tableHeaderCell} ${classes.tableIconCell}`}></TableCell>;
  if (isBulkEdit) {
    tableHeaderSecondCol = (
      <TableCell className={`${classes.tableHeaderCell} ${classes.tableIconCell}`}>
        <Box sx={{ display: 'flex', width: '48px', height: '48px', alignItems: 'center', justifyContent: 'center' }}>
          <Checkbox
            color="default"
            indeterminate={isBulkEditSelectedIndeterminate}
            checked={isBulkEditSelectedAll}
            onChange={(e) => onBulkEditSelectAll(e.target.checked)}
          />
        </Box>
      </TableCell>
    );
  }

  return (
    <TableHead sx={{ height: '60px' }}>
      <TableRow>
        <TableCell className={`${classes.tableHeaderCell} ${classes.tableIconCell}`}></TableCell>
        {tableHeaderSecondCol}
        {columns.map((col) => {
          const isActive = sortConfig?.key === col;
          const direction = isActive ? sortConfig?.direction ?? 'asc' : 'asc';
          return (
            <TableCell key={col} className={getTableRowCellClass(col)}>
              <TableSortLabel
                active={isActive}
                direction={direction}
                onClick={() => onSort(col)}
                hideSortIcon={false}
                IconComponent={isActive ? undefined : SwapVertIcon}
                className={classes.sortLabel}
                classes={{
                  active: classes.sortLabelActive,
                  icon: classes.sortLabelIcon,
                }}
              >
                {col}
              </TableSortLabel>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
